/**
 * Calendar Reminders Hook
 * 
 * Monitors upcoming calendar events and triggers desktop notifications
 * at the configured reminder times (e.g., 15 minutes, 5 minutes before).
 * 
 * Features:
 * - Checks events every minute
 * - Respects event reminder settings
 * - Tracks shown reminders to avoid duplicates
 * - Integrates with desktopNotifications service
 * 
 * @author TotalAccess Development Team
 * @date January 2026
 */

'use client';

import { useEffect, useRef, useCallback } from 'react';
import { desktopNotifications } from '@/lib/desktopNotifications';
import type { CalendarEvent, EventReminder } from '@/app/client-zone/modules/total-connect/types';
import { parseISO, differenceInMinutes, format, isAfter, isBefore, addMinutes } from 'date-fns';

interface UseCalendarRemindersOptions {
  enabled?: boolean;
  defaultReminderMinutes?: number; // Default reminder if event has none (15 min)
  onEventClick?: (eventId: string) => void;
}

interface UseCalendarRemindersReturn {
  clearShownReminders: () => void;
  getShownRemindersCount: () => number;
}

/**
 * Hook to monitor calendar events and show reminder notifications
 * 
 * @param events - Array of calendar events to monitor
 * @param options - Configuration options
 */
export function useCalendarReminders(
  events: CalendarEvent[],
  options: UseCalendarRemindersOptions = {}
): UseCalendarRemindersReturn {
  const {
    enabled = true,
    defaultReminderMinutes = 15,
    onEventClick
  } = options;

  // Track which reminders have been shown to avoid duplicates
  // Key format: `${eventId}-${reminderMinutes}`
  const shownReminders = useRef<Set<string>>(new Set());
  
  // Store events ref to access in interval
  const eventsRef = useRef<CalendarEvent[]>(events);
  
  // Update ref when events change
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  /**
   * Check if a specific reminder should be shown for an event
   */
  const shouldShowReminder = useCallback((
    event: CalendarEvent,
    reminderMinutes: number,
    now: Date
  ): boolean => {
    const reminderKey = `${event.id}-${reminderMinutes}`;
    
    // Already shown this reminder
    if (shownReminders.current.has(reminderKey)) {
      return false;
    }

    const eventStart = typeof event.start === 'string' 
      ? parseISO(event.start) 
      : new Date(event.start);

    // Calculate the reminder time
    const reminderTime = addMinutes(eventStart, -reminderMinutes);
    
    // Check if we're within the reminder window (reminder time +/- 30 seconds)
    const diffFromReminder = Math.abs(differenceInMinutes(now, reminderTime));
    
    // Show if we're within 1 minute of the reminder time
    // and the event hasn't started yet
    return diffFromReminder < 1 && isAfter(eventStart, now);
  }, []);

  /**
   * Show a reminder notification for an event
   */
  const showReminder = useCallback((
    event: CalendarEvent,
    minutesUntilStart: number
  ) => {
    const reminderKey = `${event.id}-${minutesUntilStart}`;
    
    // Mark as shown immediately to prevent duplicates
    shownReminders.current.add(reminderKey);

    const eventStart = typeof event.start === 'string' 
      ? parseISO(event.start) 
      : new Date(event.start);

    // Format the start time
    const timeStr = format(eventStart, 'h:mm a');
    
    // Build notification body
    let body = `Starting at ${timeStr}`;
    if (minutesUntilStart > 0) {
      body = `In ${minutesUntilStart} minute${minutesUntilStart !== 1 ? 's' : ''} (${timeStr})`;
    }

    // Show the notification
    desktopNotifications.showCalendarNotification({
      eventTitle: event.title,
      startTime: body,
      location: event.location,
      eventId: event.id,
      onClick: () => {
        onEventClick?.(event.id);
      }
    });

    console.log(`[CalendarReminder] Showed reminder for "${event.title}" - ${minutesUntilStart} min before`);
  }, [onEventClick]);

  /**
   * Check all events for due reminders
   */
  const checkReminders = useCallback(() => {
    if (!enabled) return;

    const now = new Date();
    const currentEvents = eventsRef.current;

    currentEvents.forEach(event => {
      // Skip cancelled events
      if (event.status === 'cancelled') return;

      // Get event start time
      const eventStart = typeof event.start === 'string' 
        ? parseISO(event.start) 
        : new Date(event.start);

      // Skip past events
      if (isBefore(eventStart, now)) return;

      // Get reminders for this event
      const reminders: number[] = [];
      
      if (event.reminders && event.reminders.length > 0) {
        // Use event's configured reminders (only popup type)
        event.reminders
          .filter((r: EventReminder) => r.method === 'popup')
          .forEach((r: EventReminder) => reminders.push(r.minutes));
      }

      // Add default reminder if no reminders configured
      if (reminders.length === 0) {
        reminders.push(defaultReminderMinutes);
      }

      // Check each reminder time
      reminders.forEach(reminderMinutes => {
        if (shouldShowReminder(event, reminderMinutes, now)) {
          const minutesUntilStart = Math.round(differenceInMinutes(eventStart, now));
          showReminder(event, minutesUntilStart);
        }
      });
    });
  }, [enabled, defaultReminderMinutes, shouldShowReminder, showReminder]);

  // Set up interval to check reminders every minute
  useEffect(() => {
    if (!enabled) return;

    // Check immediately on mount
    checkReminders();

    // Then check every 30 seconds for more responsive reminders
    const interval = setInterval(checkReminders, 30000);

    return () => clearInterval(interval);
  }, [enabled, checkReminders]);

  // Clear old reminders when events change significantly
  useEffect(() => {
    // Clean up reminders for events that no longer exist
    const currentEventIds = new Set(events.map(e => e.id));
    const reminderKeys = Array.from(shownReminders.current);
    
    reminderKeys.forEach(key => {
      const eventId = key.split('-')[0];
      if (!currentEventIds.has(eventId)) {
        shownReminders.current.delete(key);
      }
    });
  }, [events]);

  return {
    clearShownReminders: () => {
      shownReminders.current.clear();
    },
    getShownRemindersCount: () => shownReminders.current.size
  };
}

export default useCalendarReminders;
